import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getMinimalSchools, getUsers, updateUserRoles } from '@/lib/api-calls';
import { QueryKeys } from '@/lib/queries';

import { isStudent, isStudentRole, studentOfSchool } from '@airreading/common';
import {
  UserRoleAssignmentOptions,
  UserWithRolesAndProfiles,
  DEPRECATED_allPagesModifier,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface ManageStudentProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

const formSchema = z.object({
  schoolId: z.string({ required_error: 'School selection is required' }),
});

const NoSchoolOption = 'no_school';

export const ManageStudent = ({ user, trigger }: ManageStudentProps) => {
  const { data, isPending: isLoadingSchools } = useQuery(getMinimalSchools(DEPRECATED_allPagesModifier));

  const { mutateAsync } = useMutation({
    mutationFn: updateUserRoles,
  });

  const schools = data?.schools || [];
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isUserStudent = isStudent(user);

  const schoolOptions = isUserStudent ? [NoSchoolOption, ...schools.map((s) => s.id)] : [...schools.map((s) => s.id)];

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { schoolId } = formData;
    const newRoles: UserRoleAssignmentOptions = user.userRoles.filter((role) => !isStudentRole(role));

    if (schoolId !== NoSchoolOption) {
      newRoles.push(studentOfSchool(schoolId));
    }

    return mutateAsync({
      userId: user.id,
      roles: newRoles.map(({ role, schoolId }) => ({ role, schoolId })),
    })
      .then(() => {
        toast({
          title: 'Student updated successfully',
        });
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        closeModal();
      })
      .catch(() => {
        toast({
          title: 'Student update failed',
        });
      });
  };

  return (
    <FormModal
      modalTitle="Manage Student"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={{ schoolId: user.userRoles.find(isStudentRole)?.schoolId ?? undefined }}
    >
      {({ form }) => {
        return (
          <div className="my-4">
            <FormSingleSelect
              name="schoolId"
              label="School"
              form={form}
              options={schoolOptions}
              renderOption={(id) =>
                id === NoSchoolOption
                  ? 'Remove student from current school'
                  : schools.find((s) => s.id === id)?.name ?? id
              }
              loading={isLoadingSchools}
              triggerProps={{ className: 'w-[80%]' }}
            />
          </div>
        );
      }}
    </FormModal>
  );
};
